import AddIcon from '@mui/icons-material/Add'

import {
  useDeleteWarrantyRegistration,
  useGetWarrantyRegistrationsInfinite,
} from '../../../hooks/api/warranty'
import { useNavigate } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { numberFormatter } from '../../../utils/formatters'
import { Autocomplete, Grid, TextField } from '@mui/material'
import WarrantyRegistrationView from './WarrantyRegistrationView'
import Status from '../../ui/Status'
import { useAuth } from '../../../hooks/context/UserAuthContext'
import InfiniteList from '../../ui/InfiniteList'
import { useState } from 'react'
import { dealershipBrandOptions } from '../../../constants/dealershipBrands'
import { useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { DialogYesNo } from '../../ui/DialogYesNo'

const headCells = ({ showDealershipName }) => [
  {
    id: '_id',
    numeric: false,
    sortable: false,
    label: 'ID',
  },
  showDealershipName && {
    id: 'customer.dealershipName',
    numeric: false,
    sortable: true,
    label: 'Dealership Name',
    view: (data) => data.customer?.dealershipName,
  },
  {
    id: 'dateOfPurchase',
    numeric: false,
    sortable: true,
    label: 'Tyre Purchase Date',
    view: (data) => {
      try {
        return format(parseISO(data.dateOfPurchase), 'dd/MM/yyyy')
      } catch (err) {
        console.error(err)
      }
      return ''
    },
  },
  {
    id: 'mileageOnPurchase',
    numeric: true,
    sortable: true,
    label: 'Mileage',
    view: (data) => <>{numberFormatter.format(data.mileageOnPurchase)} km</>,
  },
  {
    id: 'chassisNumber',
    sortable: true,
    label: 'Chassis No.',
  },
  {
    id: 'vehicleNumber',
    sortable: true,
    label: 'Vehicle No.',
  },
  {
    id: 'salesInvoiceNumber',
    sortable: true,
    label: 'Sales Invoice',
  },
  {
    id: 'createdAt',
    sortable: true,
    label: 'Created',
    view: (data) => {
      try {
        return format(parseISO(data.createdAt), 'dd/MM/yyyy')
      } catch (err) {
        console.error(err)
      }
      return ''
    },
  },
  {
    id: 'tyres.*.claim.status',
    label: 'Claim Status',
    view: (data) => (
      <Status
        status={
          Object.values(data.tyres || {})
            .map(({ claim }) => claim?.status)
            .filter(Boolean)
            .sort((a, b) => parseISO(b.updatedAt) - parseISO(a.updatedAt))[0] || 'Nil'
        }
      />
    ),
  },
]

export function WarrantyRegistrationsTable() {
  const { user, canRegister } = useAuth()
  const navigate = useNavigate()
  const showDealershipName = user?.isAdmin

  const [brands, setBrands] = useState([])

  const queryClient = useQueryClient()
  const { mutateAsync: deleteWarrantyRegistration } = useDeleteWarrantyRegistration()
  const { enqueueSnackbar } = useSnackbar()

  const [idToDelete, setIdToDelete] = useState(null)
  const [openDelete, setOpenDelete] = useState(false)
  const onDelete = async (id) => {
    setIdToDelete(id)
    setOpenDelete(true)
  }

  const onDeleteConfirm = async (id) => {
    try {
      setOpenDelete(false)
      const res = await deleteWarrantyRegistration(id)
      queryClient.invalidateQueries(['warranty'])
      enqueueSnackbar(`Warranty registration ${res?.data?.id} has been deleted successfully.`, {
        variant: 'success',
      })
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: 'error' })
    }
  }

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            value={brands}
            onChange={(_, value) => {
              setBrands(value)
            }}
            options={dealershipBrandOptions}
            getOptionLabel={(option) => option?.label}
            renderInput={(params) => <TextField {...params} label='Filter Brands' />}
          />
        </Grid>
      </Grid>
      <InfiniteList
        queryParams={{ brands: brands?.map(({ id }) => id)?.join(',') }}
        useInfiniteQuery={useGetWarrantyRegistrationsInfinite}
        headCells={headCells({ showDealershipName })}
        component={WarrantyRegistrationView}
        onNew={canRegister ? () => navigate('/register-warranty') : undefined}
        newIcon={<AddIcon />}
        newLabel='Register'
        expand
        onDelete={onDelete}
        showExportButton={true}
      />
      <DialogYesNo
        open={openDelete}
        title='Are you sure?'
        prompt={`Do you want to delete ${idToDelete}? This process cannot be undone.`}
        textYes='Yes, delete it'
        onClose={() => setOpenDelete(false)}
        onConfirm={() => onDeleteConfirm(idToDelete)}
      />
    </>
  )
}
